import {
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import './App.scss'
import { SideBar } from './components'
import AppRouter from './components/AppRouter'
// import { Search } from './components/Search'
import { authStore } from './store/auth'
import { useLocation, useNavigate } from 'react-router-dom'
import {
	RNCommon,
	RNLogin,
	RNLogout,
	RNRegister,
	RNSuccess,
	RNWelcome
} from './helpers/routeNames'
import Cookies from 'universal-cookie'
import { userStore } from './store/user'
import { carrierStore } from './store/carrier'
import { sensorStore } from './store/sensor'
import { io } from 'socket.io-client'
import { WS_BASE_URL } from './config'
import { loggingStore } from './store/logging'
import { SensorData } from './helpers/types/sensorData'
import { notificationStore } from './store/notification'
import { ConfigProvider, Flex, notification, Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import { canvasStore } from './store/canvas'
import { CanvasData } from './helpers/types/canvas'
import { carrierPermissionStore } from './store/carrierPermission'
import { balanceStore } from './store/balance'
import { DeltaData } from './helpers/types/deltaData'
import { deltaStore } from './store/delta'
import { utilityStore } from './store/utils'
import { css } from '@emotion/css'

import 'swiper/css'
import { useTelegram } from './store/telegram'

function App() {
	const cookie = useMemo(() => new Cookies(), [])
	// const socketRef = useRef<null | Socket>(null)
	const { isLoggedIn, refresh, userinfo, logout, user, tgLogin } =
		authStore()
	const { fetchUsers, users } = userStore()
	const {
		fetchCarriers,
		carriers,
		updateProblemsCount,
		updateCarrierStatus
	} = carrierStore()
	const { sensors, setNewSensorData, setBulkSensorData } =
		sensorStore()
	const {
		fetchCanvas,
		canvas,
		setNewCanvasData,
		setBulkCanvasData
	} = canvasStore()
	const { addLogging } = loggingStore()
	const {
		fetchNotificationCount,
		addNotification,
		setIsOpen: setIsNotificationModalOpen,
		isCountFetched
	} = notificationStore()
	const { setNewDeltaData, setBulkDeltaData } = deltaStore()
	const { fetchCarrierPermission } = carrierPermissionStore()
	const { setBalance, fetchListBalance } = balanceStore()
	const { tg } = useTelegram()

	const navigate = useNavigate()
	const location = useLocation()

	const [startParam, setStartParam] = useState<string | undefined>(
		tg?.initDataUnsafe?.start_param
	)
	const [isAuthChecked, setIsAuthChecked] = useState(false)

	const { t: translate } = useTranslation()
	const t = useMemo(() => translate, [translate])

	const [api, contextHolder] = notification.useNotification({
		stack: { threshold: 3 }
	})

	const { sideBarShown, setSideBarShown } = utilityStore()

	useEffect(() => {
		if (window?.Telegram?.WebApp.initData) {
			tg.ready()
			tg.setHeaderColor('#EBEFF6')
			tg.setBackgroundColor('#EBEFF6')
			tg.disableVerticalSwipes()
			tg.enableClosingConfirmation()
			tg.expand()

			if (isLoggedIn && cookie.get('access')) {
				if (
					startParam &&
					startParam.startsWith('userRequestResponseToId-')
				) {
					navigate(
						RNSuccess +
							RNRegister +
							'/ask?id=' +
							startParam.replace(
								'userRequestResponseToId-',
								''
							)
					)
					return setStartParam('')
				}
			} else {
				const checkTgUserId = async () => {
					try {
						await tgLogin(window.Telegram.WebApp)
					} catch (err) {
						console.error('Error in tgLogin:', err)
					}
				}
				checkTgUserId()
			}
		}
	}, [tgLogin, navigate, startParam, isLoggedIn, tg, cookie])

	useEffect(() => {
		if (!user?.id) return

		// if (socketRef.current !== null) return
		const socket = io(WS_BASE_URL, {
			transports: ['websocket', 'polling'],
			withCredentials: true
		})
		// socketRef.current = socket

		socket.on('sensorData', (data: SensorData) => {
			setNewSensorData(data)
		})

		socket.on(
			'sensorDataBulk',
			(data: {
				carrierId: number
				sensorData: SensorData[]
			}) => {
				setBulkSensorData(data.carrierId, data.sensorData)
			}
		)

		socket.on('logging', (data) => {
			addLogging(data)
			if (data.activeProblemsCount !== undefined) {
				updateProblemsCount(
					data.carrierId,
					data.activeProblemsCount
				)
			}
		})

		socket.on('notification', (data) => {
			addNotification(data)
			const notificationMessage = {
				message: t('New notification'),
				description: data.title,
				onClick: () => {
					setIsNotificationModalOpen(true)
					api.destroy(`notification${data.id}`)
				},
				style: {
					cursor: 'pointer'
				},
				key: `notification${data.id}`
			}
			if (data.type === 'error') {
				api.error(notificationMessage)
			} else if (data.type === 'success') {
				api.success(notificationMessage)
			} else if (data.type === 'warning') {
				api.warning(notificationMessage)
			} else {
				api.info(notificationMessage)
			}
		})

		socket.on(
			'canvasData',
			(data: CanvasData['canvasData'][0]) => {
				setNewCanvasData(data)
			}
		)

		socket.on(
			'deltaData',
			(data: DeltaData & { isPCH?: boolean }) => {
				setNewDeltaData(data)
			}
		)

		socket.on(
			'deltaDataBulk',
			(data: { carrierId: number; data: DeltaData[] }) => {
				setBulkDeltaData({
					carrierId: data.carrierId,
					deltaData: data.data
				})
			}
		)

		socket.on(
			'canvasDataBulk',
			(data: {
				carrierId: number
				data: CanvasData['canvasData'][0][]
			}) => {
				setBulkCanvasData(data.carrierId, data.data)
			}
		)

		socket.on('balance', (data) => {
			setBalance(data)
		})

		socket.on(
			'carrierProblems',
			(data: {
				carrierId: number
				activeProblemsCount: number
			}) => {
				updateProblemsCount(
					data.carrierId,
					data.activeProblemsCount
				)
			}
		)

		socket.on(
			'carrierStatus',
			(data: {
				carrierId: number
				status: 'online' | 'offline'
				lastSeenAt: string | null
				networkOperator: string | null
				networkSignalLevel: number | null
				networkType: string | null
			}) => {
				updateCarrierStatus(
					data.carrierId,
					data.status,
					data.lastSeenAt,
					data.networkOperator,
					data.networkSignalLevel,
					data.networkType
				)
			}
		)

		return () => {
			socket.disconnect()
		}
	}, [
		addLogging,
		setNewSensorData,
		setBulkSensorData,
		setNewCanvasData,
		setBulkCanvasData,
		addNotification,
		setBalance,
		setNewDeltaData,
		setBulkDeltaData,
		updateProblemsCount,
		updateCarrierStatus,
		t,
		user?.id,
		api,
		navigate,
		setIsNotificationModalOpen
	])

	// useEffect(() => {
	// 	const tgInitiated = async () => {
	// 		console.log((window as any).Telegram)
	// 		if (
	// 			Object.keys(
	// 				(window as any)?.Telegram?.WebApp?.initDataUnsafe
	// 			).length
	// 		) {
	// 			await tgLogin
	// 		}
	// 	}

	// 	tgInitiated()
	// }, [])

	const locationRef = useRef(location)
	locationRef.current = location

	useEffect(() => {
		const init = async () => {
			try {
				const hasAccessToken = !!cookie.get('access')
				const hasRefreshToken = !!cookie.get('refresh')
				const hasTelegramData = tg.initData.length > 0

				if (!hasAccessToken && !hasTelegramData) {
					if (hasRefreshToken) {
						try {
							console.log(
								'🔄 Refreshing tokens with refresh token...'
							)
							await refresh()
							console.log(
								'✅ Tokens refreshed successfully'
							)

							const { locale } = await userinfo()
							if (
								localStorage.getItem('language') ===
								null
							) {
								localStorage.setItem(
									'language',
									locale
								)
							}

							if (
								locationRef.current.pathname ===
								RNLogin
							) {
								navigate(RNWelcome)
							}
						} catch (error) {
							console.error(
								'❌ Token refresh failed:',
								error
							)
							if (
								locationRef.current.pathname !==
									RNLogin &&
								locationRef.current.pathname !==
									RNLogout &&
								!locationRef.current.pathname.startsWith(
									RNCommon
								)
							) {
								navigate(
									'/login?path=' +
										encodeURIComponent(
											locationRef.current
												.pathname +
												locationRef.current
													.search +
												locationRef.current
													.hash
										)
								)
							}
						}
					} else {
						if (
							locationRef.current.pathname !==
								RNLogin &&
							locationRef.current.pathname !==
								RNLogout &&
							!locationRef.current.pathname.startsWith(
								RNCommon
							)
						) {
							navigate(
								'/login?path=' +
									encodeURIComponent(
										locationRef.current.pathname +
											locationRef.current
												.search +
											locationRef.current.hash
									)
							)
						}
					}
				} else if (!hasAccessToken && hasTelegramData) {
					return
				} else if (hasAccessToken) {
					try {
						const { locale } = await userinfo()
						if (
							localStorage.getItem('language') === null
						) {
							localStorage.setItem('language', locale)
						}
					} catch (error) {
						console.error(
							'❌ Failed to get user info:',
							error
						)
						if (hasRefreshToken) {
							try {
								console.log(
									'🔄 Access token invalid, trying refresh...'
								)
								await refresh()
								console.log(
									'✅ Tokens refreshed after userinfo failure'
								)
							} catch (refreshError) {
								console.error(
									'❌ Refresh also failed:',
									refreshError
								)
								logout()
							}
						} else {
							logout()
						}
					}
				}
			} finally {
				setIsAuthChecked(true)
			}
		}
		init()
	}, [
		navigate,
		refresh,
		userinfo,
		logout,
		tg.initData.length,
		cookie
	])

	useEffect(() => {
		if (
			!!cookie.get('access') &&
			location.pathname !== RNLogin &&
			!users.length &&
			!carriers.length &&
			!sensors.length &&
			!canvas.length
		) {
			const initials = async () => {
				await fetchUsers()
				await fetchCarriers()

				await fetchCanvas()
			}
			initials()
		}
	}, [
		fetchUsers,
		fetchCarriers,
		fetchCanvas,
		location.pathname,
		cookie,
		users.length,
		carriers.length,
		sensors.length,
		canvas.length
	])

	useEffect(() => {
		if (
			!isLoggedIn ||
			location.pathname === RNLogout ||
			location.pathname === RNLogin ||
			isCountFetched
		)
			return
		const initials = async () => {
			if (!user || !carriers.length) return
			await fetchNotificationCount()
			await fetchCarrierPermission(user.id)
			await fetchListBalance()
		}
		initials()
	}, [
		isLoggedIn,
		fetchNotificationCount,
		location.pathname,
		isCountFetched,
		carriers.length,
		user,
		fetchCarrierPermission,
		fetchListBalance
	])

	useEffect(() => {
		const handleMediaChange = (x: any) => {
			if (x.matches) {
				setSideBarShown(false)
			} else {
				setSideBarShown(true)
			}
		}

		const x = window.matchMedia('(max-width: 768px)')

		handleMediaChange(x)

		x.addEventListener('change', handleMediaChange)
		return () => {
			x.removeEventListener('change', handleMediaChange)
		}
	}, [setSideBarShown])

	const { getPrefixCls } = useContext(ConfigProvider.ConfigContext)
	const rootPrefixCls = getPrefixCls()
	const linearGradientButton = css`
		&.${rootPrefixCls}-btn-primary:not([disabled]):not(
				.${rootPrefixCls}-btn-dangerous
			) {
			border-width: 0;

			> span {
				position: relative;
			}

			&::before {
				content: '';
				background: linear-gradient(135deg, #6253e1, #04befe);
				position: absolute;
				inset: 0;
				opacity: 1;
				transition: all 0.3s;
				border-radius: inherit;
			}

			&:hover::before {
				opacity: 0;
			}
		}
	`

	return (
		<ConfigProvider
			direction="ltr"
			locale={localStorage.getItem('language') as any}
			button={{
				className: linearGradientButton
			}}>
			{!isAuthChecked ? (
				<Flex
					align="center"
					justify="center"
					style={{ height: '100vh', width: '100vw' }}>
					<Spin size="large" />
				</Flex>
			) : (
				<>
					{(!!isLoggedIn || !!cookie.get('access')) && (
						<>
							{contextHolder}
							{sideBarShown && <SideBar />}
							<section
								style={{
									overflow: 'auto',
									height: '100vh'
								}}>
								<AppRouter
									sideBarShown={sideBarShown}
									isLoggedIn={
										!!isLoggedIn ||
										!!cookie.get('access')
									}
								/>
							</section>
						</>
					)}
					{!isLoggedIn && !cookie.get('access') && (
						<AppRouter
							isLoggedIn={isLoggedIn}
							sideBarShown={sideBarShown}
						/>
					)}
				</>
			)}
		</ConfigProvider>
	)
}

export default App
